import moment from 'moment';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import People from '../../../common/Icons/People';
import Door from '../../../common/Icons/Door';
import Restaurant from '../../../common/Icons/Restaurant';
import Clock from '../../../common/Icons/Clock';
import tailwindConfig from '../../../../../tailwind.config';
import Button from '../../../common/Button';
import PlainTextInformation from '../../custom-information/PlainTextInformation';
import { StayDate } from '../../../../core/cancellation/getBooking';
import { CUSTOMER_SUPPORT_EMAIL, GuestCategory, Locale } from '../../../../core/constants';
import ImageWithFallback from '../../../common/ImageWithFallback';
import { HOTEL_STATUS, HotelStatus } from '../../../../../types/HotelFromApi';
import Link from '../../../common/Link/Link';
import SkipTranslationText from '../../../common/SkipTranslationText';
import SkipTranslationWrapper from '../../../common/SkipTranslationWrapper';

/**
 * Return the total number of adults and children from rooms.
 */
export const getAdultAndChildCountFromRooms = (rooms: StayDate['rooms']) => {
  return rooms?.reduce(
    (acc, room) => {
      const adultCountInRoom = room.guests.filter(
        (guest) =>
          guest.category === GuestCategory.ADULT_FEMALE ||
          guest.category === GuestCategory.ADULT_MALE
      ).length;

      const childCountInRoom = room.guests.filter(
        (guest) =>
          guest.category !== GuestCategory.ADULT_FEMALE &&
          guest.category !== GuestCategory.ADULT_MALE
      ).length;

      return {
        adultCount: acc.adultCount + adultCountInRoom,
        childCount: acc.childCount + childCountInRoom,
      };
    },
    { adultCount: 0, childCount: 0 }
  );
};

type Props = {
  hotelName: string;
  hotelId?: string;
  roomName?: string;
  roomImageUrl: string;
  checkinDate: string;
  checkinTime: string;
  checkoutDate: string;
  checkoutTime: string;
  isCancelled: boolean;
  isUpcomingBooking: boolean;
  isUpdatedByAdmin?: boolean;
  customInformation?: {
    plain?: string;
    rich?: {
      title: string;
      content: string;
    }[];
  };
  credentials?: {
    bookingId: string;
  };
  button?: {
    onClick: () => void;
    text: string;
  };
  stayDates?: StayDate[];
  hotelStatus?: HotelStatus;
  tel?: string;
  isArchived?: boolean;
};

const ConfirmationCardDesktop = ({
  hotelName,
  hotelId,
  roomName,
  roomImageUrl,
  checkinDate,
  checkinTime,
  checkoutDate,
  checkoutTime,
  isCancelled,
  isUpcomingBooking,
  isUpdatedByAdmin,
  credentials,
  button,
  customInformation,
  stayDates,
  hotelStatus,
  tel,
  isArchived,
}: Props) => {
  const { t } = useTranslation(['common', 'component']);
  const router = useRouter();

  const showBookingState = () => {
    if (isCancelled) return t('common:booking.state.cancelled');
    if (!isUpcomingBooking && !isCancelled) return t('common:booking.state.stayed');
    return t('common:booking.state.booked');
  };

  const isGrayScale = isCancelled || !isUpcomingBooking;
  const isHotelChurned = hotelStatus === HOTEL_STATUS.CHURNED;

  return (
    <div className="flex rounded-md bg-white shadow-md">
      <div
        className={`w-5 ${
          isGrayScale ? 'bg-gray-600' : 'bg-orange-700'
        } rounded-l-md rounded-r-[20px]`}
      />
      <div className="w-full p-[30px]">
        <p className={`mb-2 text-small ${isGrayScale ? 'text-gray-600' : 'text-orange-700'}`}>
          {showBookingState()}
        </p>
        <div className="flex flex-row">
          <div
            className={`flex flex-1 flex-col border-r border-r-gray-200 pr-7 ${
              isGrayScale && 'grayscale'
            }`}
          >
            <div className="relative h-44 w-full">
              <ImageWithFallback
                src={roomImageUrl || '/fallback-img-790x394.png'}
                alt="selected_hotel_room_pic"
                fallbackSrc="/fallback-img-790x394.png"
                layout="fill"
                objectFit="cover"
                className="rounded-md"
              />
            </div>
            <div className="mt-5 flex flex-row content-center">
              <div className="mr-3 block w-auto">
                <Clock size={20} color={tailwindConfig.theme.colors.gray[300]} />
              </div>
              <p className="text-mini text-gray-950">
                {t('component:confirmation_card_desktop.check_in_out')}
              </p>
            </div>
            <div className="mt-3 flex flex-row">
              <div className="mr-5 flex flex-col">
                <SkipTranslationWrapper tag="p" className="text-regular font-bold text-gray-950">
                  {checkinDate}
                </SkipTranslationWrapper>
                <SkipTranslationWrapper tag="p" className="text-regular font-bold text-gray-950">
                  {checkoutDate}
                </SkipTranslationWrapper>
              </div>
              <div className="flex flex-col">
                <SkipTranslationWrapper tag="p" className="text-regular font-bold text-gray-950">
                  {checkinTime}
                </SkipTranslationWrapper>
                <SkipTranslationWrapper tag="p" className="text-regular font-bold text-gray-950">
                  {checkoutTime}
                </SkipTranslationWrapper>
              </div>
            </div>
          </div>

          <div className="flex flex-2 flex-col pl-7">
            {credentials ? (
              <div className="mb-5 flex flex-col rounded-md bg-gray-50 p-3">
                <p className="mb-2 text-center text-large font-bold text-gray-950">
                  <SkipTranslationText
                    i18nKey="component:confirmation_card_desktop.booking.id"
                    values={{ bookingId: credentials.bookingId }}
                  />
                </p>
                <p className="text-center text-mini text-gray-600">
                  {t('component:confirmation_card_desktop.booking.remark')}
                </p>
              </div>
            ) : null}
            <div className="flex flex-1 flex-col gap-2 xl:flex-row">
              <div className="w-full">
                <p className="mb-4 text-large font-bold">
                  {hotelId ? <Link href={`/h/${hotelId}`}>{hotelName}</Link> : hotelName}
                </p>
                {stayDates?.map((stayDate, index) => {
                  const { adultCount, childCount } =
                    getAdultAndChildCountFromRooms(stayDate.rooms) || {};

                  return (
                    <div
                      className="flex flex-col gap-y-2 py-4"
                      style={{
                        borderTop: index ? '1px dashed #D8D9D9' : undefined,
                      }}
                      key={stayDate.date}
                    >
                      <div className="flex flex-row items-center">
                        <SkipTranslationWrapper tag="p" className="line-clamp-1">
                          {stayDate.date}
                        </SkipTranslationWrapper>
                      </div>
                      {roomName && (
                        <div className="flex flex-row items-center">
                          <div>
                            <Door size={20} />
                          </div>
                          <p className="ml-4 line-clamp-1">{roomName}</p>
                        </div>
                      )}
                      <div className="flex flex-row items-center">
                        <div>
                          <People size={20} color="#868686" />
                        </div>
                        <p className="ml-4 line-clamp-1">
                          <SkipTranslationText
                            i18nKey="common:label.room_count"
                            values={{ count: stayDate.rooms?.length }}
                          />
                          /
                          <SkipTranslationText
                            i18nKey="common:label.adult_count"
                            values={{ count: adultCount }}
                          />
                          ・
                          <SkipTranslationText
                            i18nKey="common:label.child_count"
                            values={{ count: childCount }}
                          />
                        </p>
                      </div>
                      {stayDate.planName && (
                        <div className="flex flex-row items-center">
                          <div>
                            <Restaurant size={20} />
                          </div>
                          <p className="ml-4 line-clamp-1">{stayDate.planName}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
                {isArchived && (
                  <>
                    <p className="text-orange-700">
                      {t('component:confirmation_card.archived.title')}
                    </p>
                    <p className="whitespace-pre-line">
                      {t('component:confirmation_card.archived.description')}
                    </p>
                    <br />
                    {router.locale === Locale.JAPANESE ? (
                      <p>{tel}</p>
                    ) : (
                      <Link href={`mailto:${CUSTOMER_SUPPORT_EMAIL}`}>
                        <a className="text-orange-700 underline">{CUSTOMER_SUPPORT_EMAIL}</a>
                      </Link>
                    )}
                  </>
                )}
                {isHotelChurned && (isCancelled || isUpcomingBooking) && (
                  <>
                    <p className="text-orange-700">
                      {t('component:confirmation_card_desktop.hotel_churned')}
                    </p>
                    {isCancelled ? (
                      <p>
                        {t('component:confirmation_card_desktop.hotel_churned.booking_cancelled')}
                      </p>
                    ) : (
                      <p className="whitespace-pre-line">
                        {t('component:confirmation_card_desktop.hotel_churned.booking_active')}
                      </p>
                    )}
                    <br />
                    <p>{tel}</p>
                  </>
                )}
                <div className="flex flex-1 items-end justify-end">
                  {!!button && (
                    <Button
                      onClick={button.onClick}
                      disabled={isUpdatedByAdmin}
                      color="white"
                      textColor="red"
                      className="mt-2 shrink-0 border border-orange-700"
                    >
                      {button.text}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {moment(checkoutDate).isAfter(moment()) && !isCancelled && (
          <PlainTextInformation
            bold
            message={customInformation?.plain}
            title={t('component:confirmation_card_desktop.plain_text_information.title')}
          />
        )}
      </div>
    </div>
  );
};

export default ConfirmationCardDesktop;
